import { useMemo } from 'react';

import { coordsOf, Funeral, PickupLocation } from './pickupLocation';
import { usePickupLocation } from './usePickupLocation';
import { LatLng, testZone, ZoneRegion } from './zone';

export type ZoneStatus =
  | { state: 'no_address' }
  /** An address, but no coordinates — finding them costs a geocode. */
  | { state: 'not_located'; pickup: PickupLocation }
  | { state: 'in_zone'; pickup: PickupLocation; municipality: string; coords: LatLng }
  | { state: 'out_of_zone'; pickup: PickupLocation; coords: LatLng };

/**
 * Whether a case's pickup is inside Zone 1.
 *
 * Shared so the contract and the funeral cannot disagree about a case — they
 * are two views of one answer, and the cost of them drifting is a family told
 * one thing on the case and billed another on the contract.
 *
 * Only the free half lives here. Point-in-polygon is local and instant, so it
 * runs wherever it is asked for. Geocoding an address without coordinates, and
 * routing to get a distance, both cost money and produce a price — those stay
 * behind a deliberate action on the contract.
 */
export function useZoneStatus(
  funeral: Funeral & { region?: string | null },
): ZoneStatus {
  const pickup = usePickupLocation(funeral);

  return useMemo(() => {
    if (!pickup) return { state: 'no_address' };

    const coords = coordsOf(pickup.address);
    if (!coords) return { state: 'not_located', pickup };

    const region: ZoneRegion =
      (funeral.region ?? '').toLowerCase() === 'ontario' ? 'ontario' : 'quebec';
    const zone = testZone(coords, region);

    return zone.inZone
      ? { state: 'in_zone', pickup, municipality: zone.municipality, coords }
      : { state: 'out_of_zone', pickup, coords };
  }, [funeral, pickup]);
}
